/* eslint-disable */
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  coordination,
  project,
  useCreateProjectContactMutation,
  useCreateProjectMutation,
  useCreateProjectTeamMutation,
  useCreateRoleMutation,
  useDeleteProjectContactMutation,
  useDeleteProjectTeamMutation,
  useGetContactsQuery,
  useGetContractTypesQuery,
  useGetMarketSegmentsQuery,
  useGetOrganizationsQuery,
  useGetProjectContactsQuery,
  useGetProjectStagesQuery,
  useGetProjectTeamsQuery,
  useGetProjectTemplatesQuery,
  useGetProjectTypesQuery,
  useGetRolesQuery,
  useGetSubscribersQuery,
  useGetSubscriptionContactsQuery,
  useGetTeamsQuery,
  useUpdateProjectContactMutation,
  useUpdateProjectTeamMutation,
} from 'store/api';
import {
  Attachment,
  Contact,
  Organization,
  Project,
  ProjectContact,
  ROLETYPE,
  Role,
  Subscriber,
  SubscriptionContact,
  Team,
  MarketSegment,
} from 'store/api/models';
import { JItem } from '@centerpiece/components';
import { FormikHelpers, getIn } from 'formik';
import { statesConfig, sortString } from '@centerpiece/components';
import * as Yup from 'yup';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  InputBaseComponentProps,
  Popper,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  InfoOutlined,
  RestartAlt,
  Person,
  AccountBox,
  PersonAdd,
  Add,
  CorporateFare,
  Mail,
  Badge,
  KeyboardReturn,
  Contacts,
  FolderShared,
} from '@mui/icons-material';
import { useState } from 'react';
import {
  groupOrganizationContacts,
  stakeHolderOptions,
} from 'utils/contacts.utils';
import {
  useStakeholderForm,
  useInternalRolesForm,
  customStakeholder,
  variableRoleStakeholder,
} from './useStakeholderForm';
import { useAddress } from './jfield/useAddress';

const CustomPopper = styled(Popper)(({ theme }) => ({
  // Set a larger width for the options list
  '& .MuiAutocomplete-paper': {
    minWidth: '40vw',
    width: '600px', // Set your desired width here
    maxWidth: '90vw',
  },
}));

export const useMasterProjectForm = ({
  id,
  edit = false,
  setPreview,
  setOpenContact,
}: {
  id?: number;
  edit?: boolean;
  setPreview?: Function;
  setOpenContact: Function;
}): [any, JItem[]] => {
  const stakeholderForm = useStakeholderForm(setOpenContact);
  const internalRolesForm = useInternalRolesForm(setOpenContact);
  const { data: templates = [] } = useGetProjectTemplatesQuery({});
  // const { data: stages = [] } = useGetProjectStagesQuery({})
  const { data: types = [] } = useGetProjectTypesQuery({});
  const { data: contract_types = [] } = useGetContractTypesQuery({});
  const { data: teams = [] } = useGetTeamsQuery({});
  const { data: project_teams = [], refetch: refetchProjectTeams } =
    useGetProjectTeamsQuery({ params: { project: id, nested: 'all' } });
  const { data: project_contacts = [], refetch: refetchProjectContacts } =
    useGetProjectContactsQuery({ params: { project: id, nested: 'all' } });
  const [loading, setLoading] = useState<boolean>(false);
  const { data: market_segments = [] } = useGetMarketSegmentsQuery({});
  const { data: roles = [] } = useGetRolesQuery({});
  const [createRole] = useCreateRoleMutation();
  const { data: subscribers = [] } = useGetSubscribersQuery({
    params: { nested: 'all' },
  });
  const { data: subscription_contacts = [] } = useGetSubscriptionContactsQuery({
    params: { nested: 'all' },
  });
  const { data: organizations = [] } = useGetOrganizationsQuery({
    params: { nested: 'all' },
  });
  const [deleteProjectTeam] = useDeleteProjectTeamMutation();
  const [createProjectTeam] = useCreateProjectTeamMutation();
  const [editProjectTeam] = useUpdateProjectTeamMutation();
  const [deleteProjectContact] = useDeleteProjectContactMutation();
  const [createProjectContact] = useCreateProjectContactMutation();
  const [editProjectContact] = useUpdateProjectContactMutation();
  const projectAddress = useAddress({ name: 'form' });
  return [
    Yup.object({
      title: Yup.string().required('Project name is required'),
      type: Yup.object().nullable().required('Project type is required'),
      template: !edit
        ? Yup.object()
            .required('Please select a template or specify "No Template"')
            .nullable()
        : Yup.object().nullable(),
      // type: Yup.object().required('Please select a project type').nullable(),
      // contacts: Yup.array().of(
      //   Yup.object()
      //     .shape({
      //       contact: Yup.object()
      //         .nullable()
      //         .required('Please select a contact'),
      //     })
      //     .nullable()
      // ),
      stakeholders: Yup.array().of(
        Yup.object()
          .shape({
            stakeholdertype: Yup.object()
              .nullable()
              .required('Please select a type'),
          })
          .nullable()
      ),
    }),
    [
      {
        precondition: () => !edit,
        name: 'project-templates',
        label: 'Project Template',
        itemType: 'typography',
        jItemProps: { variant: 'overline' },
      },
      {
        precondition: () => !edit,
        name: 'project-templates-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        precondition: () => !edit,
        name: 'template',
        label: 'Project Template *',
        itemType: 'autocomplete',
        options: [
          ...(templates || []),
          { id: -1, title: 'No Template' } as any,
        ],
        gridProps: (values: any) =>
          !edit && !!values?.template && !(values?.template?.id === -1)
            ? { xs: 12, sm: 6 }
            : { xs: 12 },
        jItemProps: {
          size: 'small',
          helperText: (values: any) => {
            if (values?.id !== -1) {
              return (
                <Typography variant="caption" id="tableTitle" component="div">
                  <Tooltip title={`Preview Template`}>
                    <IconButton
                      onClick={event => {
                        // event.stopPropagation()
                        // event.preventDefault()
                        setPreview && setPreview(values.id);
                      }}
                      size="small"
                    >
                      <InfoOutlined color="secondary" />
                    </IconButton>
                  </Tooltip>{' '}
                  Preview Template
                </Typography>
              );
            } else {
              return <div />;
            }
          },
          onChange: (value: any, name: string, setFieldValue: Function) => {
            setFieldValue(
              'type',
              value?.type_r ? types.find(t => t.id === value?.type_r?.id) : null
            );
          },
        },
      },
      // {
      //   precondition: (values: any) =>
      //     !edit && !!values?.template && !(values?.template?.id === -1),
      //   name: 'stage',
      //   label: 'Starting Stage (optional)',
      //   itemType: 'autocomplete',
      //   options: values =>
      //     [...values?.template?.stages].sort((a, b) => a.sequence - b.sequence),
      //   gridProps: { xs: 12, sm: 6 },
      // },
      {
        precondition: (values: any) =>
          !edit && !!values?.template && !(values?.template?.id === -1),
        name: 'assignees',
        multiple: true,
        label: 'Assign all template items (optional)',
        itemType: 'autocomplete',
        options: values => {
          return subscribers
            ?.filter(
              (s: Subscriber) =>
                !values?.assignees?.find((r: any) => s.contact_r?.id === r.id)
            )
            ?.map((subscriber: Subscriber) => ({
              id: subscriber?.contact_r.id,
              email: subscriber?.contact_r?.email,
              name: subscriber?.contact_r?.firstname
                ? `${subscriber?.contact_r?.firstname} ${subscriber?.contact_r?.lastname}`
                : subscriber?.contact_r?.email,
            }));
        },
        gridProps: { xs: 12, sm: 6 },
      },
      {
        precondition: () => !edit,
        name: 'internal-team-label',
        label: 'Project Team',
        itemType: 'typography',
        jItemProps: { variant: 'overline' },
      },
      {
        precondition: () => !edit,
        name: 'internal-team-label-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        precondition: () => !edit,
        name: 'contacts',
        itemType: 'fieldarray',
        label: 'teammates',
        array: [
          {
            name: 'contact',
            label: 'Teammate',
            // freeSolo: true,
            itemType: 'autocomplete',
            jItemProps: {
              renderOption: (props: any, option: any) => {
                return (
                  <Box component="li" {...props}>
                    <small>
                      <AccountBox
                        fontSize="inherit"
                        sx={{ mr: 1 }}
                        color="secondary"
                      />
                    </small>
                    {option.name}
                  </Box>
                );
              },
              getOptionLabel: (option: any) =>
                `${option?.firstname} ${option?.lastname}`,
            },
            options: values => {
              return subscribers
                ?.filter(
                  (s: Subscriber) =>
                    !values?.contacts?.find(
                      (r: any) => s.contact_r?.id === r.contact?.id
                    )
                )
                ?.map((subscriber: Subscriber) => {
                  // const job_title = subscriber?.contact_r?.form?.custom_fields?.find((cf:any) => cf.job_title !== '')
                  return {
                    ...subscriber.contact_r,
                    // id: subscriber?.contact_r.id,
                    // email: subscriber?.contact_r?.email,
                    name: subscriber?.contact_r?.firstname
                      ? `${subscriber?.contact_r?.firstname} ${subscriber?.contact_r?.lastname}`
                      : // ? `${subscriber?.contact_r?.firstname} ${subscriber?.contact_r?.lastname}${job_title ? ` - ${job_title}`:''}${subscriber?.contact_r?.email ? ` - ${subscriber?.contact_r?.email}`:''}`
                        subscriber?.contact_r?.email,
                  };
                });
            },
            gridProps: { xs: 12, sm: 5 },
          },
          {
            name: 'role',
            label: 'Role',
            itemType: 'autocomplete',
            // freeSolo: true,
            options: [
              ...(roles.filter(role => role.type === 'Internal') || []),
            ].sort((a, b) => sortString(a.name, b.name)),
            jItemProps: {
              getOptionLabel: (option: any) => option?.name || `Role`,
            },
            gridProps: { xs: 10, sm: 5 },
          },
          {
            name: 'form.watcher',
            label: 'Notifications',
            itemType: 'checkbox',
            jItemProps: {
              tooltip:
                'User will receive email notifications on item and project completions.',
              // sx: {
              //   boxSizing: 'border-box',
              //   px: { xs: 0.5 },
              // },
              // padding: 'none',
              // size: 'small',
            },
            gridProps: { xs: 1, sm: 2 },
          },
        ],
        gridProps: { xs: 12 },
      },
      {
        name: 'project-details',
        label: 'Project Details',
        itemType: 'typography',
        jItemProps: { variant: 'overline' },
      },
      {
        name: 'project-details-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        precondition: () => !edit,
        name: 'title',
        label: 'Project Name *',
        itemType: 'textfield',
        gridProps: { xs: 12 },
        jItemProps: {
          size: 'small',
          // InputProps: { size: 'small' },
        },
      },
      // {
      //   name: 'stage',
      //   label: 'Project Stage',
      //   itemType: 'autocomplete',
      //   options: stages,
      //   gridProps: { xs: 12, sm: 6 },
      // },
      {
        name: 'details',
        label: 'Project Description',
        itemType: 'textfield',
        jItemProps: { multiline: true, rows: 4, variant: 'outlined' },
        gridProps: { xs: 12 },
      },
      {
        // precondition: () => !edit,
        name: 'type',
        label: 'Project Type *',
        itemType: 'autocomplete',
        options: types,
        gridProps: (values: any) =>
          market_segments?.filter(
            (ms: MarketSegment) => ms?.project_type === values?.type?.id
          ).length > 0
            ? { xs: 12, sm: 6 }
            : { xs: 12, sm: 6 },
        jItemProps: {
          onChange: (value: any, name: string, setFieldValue: Function) => {
            setFieldValue('market_segment', null);
          },
        },
      },
      {
        precondition: (values: any) =>
          values?.type?.id &&
          market_segments?.filter(
            (ms: MarketSegment) => ms?.project_type === values?.type?.id
          ).length > 0,
        name: 'market_segment',
        label: 'Market Segment',
        // disabled: edit,
        itemType: 'autocomplete',
        options: (values: any) =>
          market_segments?.filter(
            (ms: MarketSegment) => ms?.project_type === values?.type?.id
          ),
        gridProps: { xs: 12, sm: 6 },
      },
      {
        name: 'form.contract_type',
        label: 'Contract Type',
        // disabled: edit,
        jItemProps: edit ? { type: 'local-disabled' } : {},
        itemType: 'autocomplete',
        options: contract_types,
        gridProps: { xs: 12, md: 6 },
      },
      {
        precondition: () => !edit,
        name: 'teams',
        multiple: true,
        label: 'Teams',
        itemType: 'autocomplete',
        options: values => {
          return (
            teams?.filter(
              (t: Team) => !values?.teams?.find((r: any) => t.id === r.id)
            ) || []
          );
        },
        gridProps: (values: any) =>
          market_segments?.filter(
            (ms: MarketSegment) => ms?.project_type === values?.type?.id
          ).length > 0
            ? { xs: 12, sm: 6 }
            : { xs: 12, sm: 6 },
        jItemProps: {
          renderTags: (value: readonly string[], getTagProps: any) => {
            return value.map((option: any, index: number) => (
              <Chip
                variant="outlined"
                color="secondary"
                label={typeof option === 'string' ? option : option?.name}
                size="small"
                {...getTagProps({ index })}
              />
            ));
          },
        },
      },
      {
        precondition: () => edit,
        name: 'teams',
        itemType: 'autocomplete',
        options: (values: any) => {
          return (
            teams?.filter(
              (t: Team) => !values?.find((tr: any) => t.id === tr?.team_r?.id)
            ) || []
          );
        },
        multiple: true,
        label: 'Team',
        loading: loading,
        gridProps: (values: any) =>
          market_segments?.filter(
            (ms: MarketSegment) => ms?.project_type === values?.type?.id
          ).length > 0
            ? { xs: 12, sm: 4 }
            : { xs: 12, sm: 6 },
        value:
          project_teams?.map(pt => ({ ...pt, name: pt?.team_r?.name })) || [],
        jItemProps: {
          disableClearable: true,
          onChange: async (items: any[]) => {
            if (items && Array.isArray(items) && items.length > 0) {
              items.forEach(async (item: any) => {
                if (!item.team) {
                  setLoading(true);
                  await createProjectTeam({ project: id, team: item.id });
                  refetchProjectTeams();
                  // props?.afterSave && props?.afterSave()
                  setLoading(false);
                }
              });
            }
          },
          renderTags: (value: readonly string[], getTagProps: any) => {
            return value.map((option: any, index: number) => (
              <Chip
                variant="outlined"
                color="secondary"
                label={typeof option === 'string' ? option : option?.name}
                size="small"
                {...getTagProps({ index })}
                onDelete={async props => {
                  setLoading(true);
                  if (option.id) {
                    await deleteProjectTeam(option.id);
                    getTagProps({ index }).onDelete(props);
                    refetchProjectTeams();
                    // props?.afterSave && props?.afterSave()
                  }
                  setLoading(false);
                }}
              />
            ));
          },
        },
      },
      {
        name: 'form.prevailing_wage',
        label: 'Prevailing Wage?',
        itemType: 'checkbox',
        jItemProps: {
          // tooltip:
          //   'User will receive email notifications on item and project completions.',
        },
        gridProps: { xs: 12, sm: 6 },
      },
      {
        precondition: (values: any) => values?.form?.prevailing_wage,
        name: 'form.prevailing_wage_date',
        label: 'Prevailing Wage Date',
        itemType: 'datepicker',
        gridProps: { xs: 12, sm: 6 },
      },
      {
        name: 'job-location-label',
        label: 'Job Location',
        itemType: 'typography',
        jItemProps: { variant: 'overline' },
      },
      {
        name: 'job-location-label-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      ...projectAddress,
      {
        name: 'project-dates',
        label: 'Project Dates',
        itemType: 'typography',
        jItemProps: { variant: 'overline' },
      },
      {
        name: 'project-dates-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        name: 'due_date',
        label: 'Expected completion date',
        itemType: 'datepicker',
        gridProps: { xs: 12, sm: 6 },
      },
      {
        name: 'form.billing_due_date',
        label: 'Billing Due Date',
        itemType: 'textfield',
        gridProps: { xs: 12, md: 6 },
      },
      {
        name: 'project-financials',
        label: 'Project Financials',
        itemType: 'typography',
        jItemProps: { variant: 'overline' },
      },
      {
        name: 'project-financials-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        // precondition: (values: any) => !disable_type && values?.type === 'Default',
        name: 'form.custom_financials',
        itemType: 'fieldarrayselect',
        label: 'Financials Field',
        options: edit
          ? [
              'Budget Financials',
              'Bid/Quote Financials',
              'Contract Financials',
              'Realized Financials',
            ]
          : [
              'Budget Financials',
              'Bid/Quote Financials',
              'Contract Financials',
            ],
        jItemProps: {
          paper: { elevation: 0 },
        },
        gridProps: {
          // sx: { paddingTop: '10px !important' },
        },
        array: [
          // {
          //   precondition: (values: any) =>
          //     !values?.form?.custom_financials?.[values.index]?.fieldtype,
          //   name: 'fieldtype',
          //   label: 'Type',
          //   // disabled: edit,
          //   jItemProps: edit ? { type: 'local-disabled' } : {},
          //   itemType: 'autocomplete',
          //   options: ['Budget Financials'],
          // },
          {
            precondition: (values: any) =>
              values?.form?.custom_financials?.['Budget Financials'] ||
              values?.form?.revenue,
            name: '',
            label: 'Budget Financials',
            itemType: 'paper',
            jItemProps: { paper: { elevation: 0 } },
            gridProps: { xs: 12 },
            array: [
              {
                name: 'form.revenue',
                label: 'Budget Revenue',
                itemType: 'textfield',
                // helpertext: "Add prices in $",
                jItemProps: {
                  type: 'formatted_number',
                  // type: 'number',
                  InputProps: {
                    startAdornment: '$',
                  },
                  inputProps: {
                    inputMode: 'numeric',
                  },
                },
                gridProps: { xs: 12, sm: 6 },
              },
              {
                name: 'form.margin',
                label: 'Budget Margin',
                itemType: 'textfield',
                // input_type: 'number',
                // helpertext: "Add win rate in %",
                jItemProps: {
                  type: 'number',
                  InputProps: {
                    startAdornment: '%',
                  },
                },
                gridProps: { xs: 12, sm: 6 },
              },
            ],
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_financials?.['Bid/Quote Financials'],
            name: '',
            label: 'Bid/Quote Financials',
            itemType: 'paper',
            jItemProps: { paper: { elevation: 0 } },
            gridProps: { xs: 12 },
            array: [
              {
                name: 'form.bidrevenue',
                label: 'Bid/Quote Revenue',
                itemType: 'textfield',
                // helpertext: "Add prices in $",
                jItemProps: {
                  type: 'formatted_number',
                  // type: 'number',
                  InputProps: {
                    startAdornment: '$',
                  },
                  inputProps: {
                    inputMode: 'numeric',
                  },
                },
                gridProps: { xs: 12, sm: 6 },
              },
              {
                name: 'form.bidmargin',
                label: 'Bid/Quote Margin',
                itemType: 'textfield',
                // input_type: 'number',
                // helpertext: "Add win rate in %",
                jItemProps: {
                  type: 'number',
                  InputProps: {
                    startAdornment: '%',
                  },
                },
                gridProps: { xs: 12, sm: 6 },
              },
            ],
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_financials?.['Contract Financials'],
            name: '',
            label: 'Contract Financials',
            itemType: 'paper',
            jItemProps: { paper: { elevation: 0 } },
            gridProps: { xs: 12 },
            array: [
              {
                name: 'form.contractrevenue',
                label: 'Contract Revenue',
                itemType: 'textfield',
                // helpertext: "Add prices in $",
                jItemProps: {
                  type: 'formatted_number',
                  // type: 'number',
                  InputProps: {
                    startAdornment: '$',
                  },
                  inputProps: {
                    inputMode: 'numeric',
                  },
                },
                gridProps: { xs: 12, sm: 6 },
              },
              {
                name: 'form.contractmargin',
                label: 'Contract Margin',
                itemType: 'textfield',
                // input_type: 'number',
                // helpertext: "Add win rate in %",
                jItemProps: {
                  type: 'number',
                  InputProps: {
                    startAdornment: '%',
                  },
                },
                gridProps: { xs: 12, sm: 6 },
              },
            ],
          },
          {
            precondition: (values: any) =>
              (values?.form?.custom_financials?.['Realized Financials'] ||
                values?.form?.realized_revenue) &&
              edit,
            name: '',
            label: 'Realized Financials',
            itemType: 'paper',
            jItemProps: { paper: { elevation: 0 } },
            gridProps: { xs: 12 },
            array: [
              {
                name: 'form.realized_revenue',
                label: 'Realized Revenue',
                itemType: 'textfield',
                // helpertext: "Add prices in $",
                jItemProps: {
                  type: 'formatted_number',
                  // type: 'number',
                  InputProps: {
                    startAdornment: '$',
                  },
                  inputProps: {
                    inputMode: 'numeric',
                    pattern: '[0-9]*', // Only allow numeric input
                  },
                },
                gridProps: { xs: 12, sm: 6 },
              },
              {
                name: 'form.realized_margin',
                label: 'Realized Margin',
                itemType: 'textfield',
                // input_type: 'number',
                // helpertext: "Add win rate in %",
                jItemProps: {
                  type: 'number',
                  InputProps: {
                    startAdornment: '%',
                  },
                },
                gridProps: { xs: 12, sm: 6 },
              },
            ],
          },
        ],
      },
      {
        precondition: (values: any) => !edit,
        name: 'stakeholder-fields',
        itemType: 'typography',
        jItemProps: {
          variant: 'overline',
          sx: { mb: '0 !important', pb: '0 !important' },
        },
        label: 'Project Stakeholders',
      },
      {
        precondition: (values: any) => !edit,
        name: 'stakeholder-fields-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      // { ...stakeholderForm, precondition: (values: any) => !edit },
      {
        precondition: (values: any) => !edit,
        name: 'stakeholders',
        itemType: 'fieldarray',
        label: '',
        // device: downMd ? 'mobile' : 'desktop',
        jItemProps: {
          addLabel: 'Add other project stakeholders',
        },
        array: [
          {
            precondition: (values: any) =>
              !values?.stakeholders?.[values.index]?.stakeholdertype,
            name: 'stakeholdertype',
            label: 'Type',
            itemType: 'autocomplete',
            options: [
              ...roles?.filter(r => r.type === 'External'),
              // { id: -1, name: 'Custom' },
            ],
            gridProps: { xs: 10, sm: 5 },
          },
          ...(roles
            ?.filter(r => r.type === 'External')
            .map(
              role =>
                variableRoleStakeholder(
                  role,
                  stakeHolderOptions({
                    subscription_contacts,
                    organizations,
                    role,
                  }),
                  setOpenContact,
                  undefined,
                  { gridProps: { xs: 10, sm: 5 } },
                  { PopperComponent: CustomPopper }
                ) as JItem
            ) || []),
          customStakeholder(
            stakeHolderOptions({
              subscription_contacts,
              organizations,
            }),
            setOpenContact,
            undefined,
            { gridProps: { xs: 10, sm: 5 } },
            { PopperComponent: CustomPopper }
          ) as JItem,
          {
            name: 'form.watcher',
            label: 'Notifications',
            itemType: 'checkbox',
            jItemProps: {
              tooltip:
                'User will receive email notifications on item and project completions.',
              sx: {
                boxSizing: 'border-box',
                px: { xs: 0.5 },
              },
              padding: 'none',
              size: 'small',
            },
            gridProps: { xs: 1, sm: 1 },
          },
        ],
      },
      {
        name: 'erp-info',
        label: 'ERP Information',
        itemType: 'typography',
        jItemProps: { variant: 'overline' },
      },
      {
        name: 'erp-info-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        name: 'form.erp_proj_id',
        label: 'Project ID',
        itemType: 'textfield',
        gridProps: { xs: 12, md: 4 },
      },
      {
        name: 'form.erp_acct_id',
        label: 'Account ID',
        itemType: 'textfield',
        gridProps: { xs: 12, md: 4 },
      },
      {
        name: 'form.erp_sales_id',
        label: 'Sales Order ID',
        itemType: 'textfield',
        gridProps: { xs: 12, md: 4 },
      },
      {
        precondition: () => !edit,
        name: 'attachments-label',
        label: 'Attachments',
        itemType: 'typography',
        jItemProps: { variant: 'overline' },
      },
      {
        precondition: () => !edit,
        name: 'attachments-label-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        precondition: () => !edit,
        name: 'attachments',
        label: 'Upload Project Documents',
        itemType: 'dropzone',
        gridProps: { xs: 12 },
      },
      {
        name: 'custom-fields',
        itemType: 'typography',
        jItemProps: {
          variant: 'overline',
          sx: { mb: '0 !important', pb: '0 !important' },
        },
        label: 'Additional Fields',
      },
      {
        name: 'additional-fields-bottom',
        label: '',
        itemType: 'divider',
        gridProps: { sx: { paddingTop: '0px !important' } },
      },
      {
        // precondition: (values: any) => !disable_type && values?.type === 'Default',
        name: 'form.custom_fields',
        itemType: 'fieldarray',
        label: 'Additional Field',
        gridProps: {
          // sx: { paddingTop: '10px !important' },
        },
        array: [
          {
            precondition: (values: any) =>
              !values?.form?.custom_fields?.[values.index]?.fieldtype,
            name: 'fieldtype',
            label: 'Type',
            // disabled: edit,
            jItemProps: edit ? { type: 'local-disabled' } : {},
            itemType: 'autocomplete',
            options: ['Owner', 'Custom'],
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Custom',
            name: 'dynamic',
            label: 'Custom Field',
            ...(!edit
              ? {
                  tooltip: {
                    title: (
                      <Typography variant="body1">
                        Please specify the title of the field
                      </Typography>
                    ),
                    open: true,
                    placement: 'top',
                    arrow: true,
                  },
                }
              : {}),
            jItemProps: {
              textFieldType: 'custom',
              // labelFunc: (name: any, values: any, formik: FormikHelpers<any>) => {
              //   return getIn(values, name) || 'Please enter the field label...'
              // },
              InputProps: {
                startAdornment: (
                  name: any,
                  values: any,
                  formik: FormikHelpers<any>
                ) => {
                  if (!getIn(values, name)?.value) {
                    return (
                      <InputAdornment position="end">
                        <Tooltip title={`Reset type`}>
                          <IconButton
                            onClick={() => {
                              formik.setFieldValue(name, undefined);
                              formik.setFieldValue(
                                name.replaceAll('dynamic', 'fieldtype'),
                                undefined
                              );
                            }}
                          >
                            <RestartAlt color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    );
                  }
                },
              },
            },
            itemType: 'textfield',
          },
          {
            precondition: (values: any) =>
              values?.form?.custom_fields?.[values.index]?.fieldtype ===
              'Owner',
            name: 'owner_name',
            label: 'Owner',
            itemType: 'textfield',
          },
          // {
          //   precondition: (values: any) => values?.form?.custom_fields?.[values.index]?.fieldtype === "Custom",
          //   name: 'value',
          //   label: 'Value',
          //   itemType: 'textfield',
          // },
          // {
          //   precondition: (values: any) => values?.form?.custom_fields?.[values.index]?.fieldtype === "Team",
          //   name: 'team',
          //   label: 'Select a Team',
          //   // disabled: edit,
          //   jItemProps: edit ? { type: 'local-disabled' } : {
          //     InputProps: {
          //       startAdornment: (name: any, values: any, formik: FormikHelpers<any>) => {
          //         return (
          //           <Tooltip title={`Reset type`}>
          //             <IconButton
          //               onClick={() => formik.setFieldValue(name.replaceAll('team', 'fieldtype'), undefined)}
          //             >
          //               <RestartAlt color='secondary' />
          //             </IconButton>
          //           </Tooltip>
          //         )
          //       },
          //     },
          //   },
          //   itemType: 'autocomplete',
          //   options: teams,
          // },
          // {
          //   precondition: (values: any) =>
          //     values?.form?.custom_fields?.[values.index]?.fieldtype ===
          //     'Market Segment',
          //   name: 'market_segment',
          //   label: 'Market Segment',
          //   // disabled: edit,
          //   jItemProps: {
          //     type: edit ? 'local-disabled' : undefined,
          //     InputProps: {
          //       startAdornment: (
          //         name: any,
          //         values: any,
          //         formik: FormikHelpers<any>
          //       ) => {
          //         if (!getIn(values, name)) {
          //           return (
          //             <Tooltip title={`Reset type`}>
          //               <IconButton
          //                 onClick={() =>
          //                   formik.setFieldValue(
          //                     name.replaceAll('market_segment', 'fieldtype'),
          //                     undefined
          //                   )
          //                 }
          //               >
          //                 <RestartAlt color="secondary" />
          //               </IconButton>
          //             </Tooltip>
          //           );
          //         }
          //       },
          //     },
          //   },
          //   itemType: 'autocomplete',
          //   options: market_segments,
          // },
        ],
      },
      // {
      //   // precondition: (values: any) => disable_type,
      //   name: 'form.custom_fields',
      //   itemType: 'jItem',
      //   label: '',
      //   gridProps: {
      //     sx: { paddingTop: '10px !important' },
      //   },
      // },
      // { ...internalRolesForm, precondition: (values: any) => !edit },

      {
        precondition: () => !edit,
        name: 'submit',
        itemType: 'button',
        jItemProps: { type: 'submit' },
        label: 'CREATE PROJECT',
        gridProps: { xs: 12, sm: 6, md: 5, mx: 'auto' },
      },
    ],
  ];
};

export const useMasterEditProjectForm = ({
  id,
  setPreview,
  setOpenContact,
}: {
  id?: number;
  setPreview?: Function;
  setOpenContact: Function;
}): [any, JItem[]] => {
  const { data: types = [] } = useGetProjectTypesQuery({});
  const { data: contract_types = [] } = useGetContractTypesQuery({});
  const { data: teams = [] } = useGetTeamsQuery({});
  const { data: project_teams = [], refetch: refetchProjectTeams } =
    useGetProjectTeamsQuery({ params: { project: id, nested: 'all' } });
  const { data: project_contacts = [], refetch: refetchProjectContacts } =
    useGetProjectContactsQuery({ params: { project: id, nested: 'all' } });
  const [loading, setLoading] = useState<boolean>(false);
  const { data: market_segments = [] } = useGetMarketSegmentsQuery({});
  const [createRole] = useCreateRoleMutation();
  const [deleteProjectTeam] = useDeleteProjectTeamMutation();
  const [createProjectTeam] = useCreateProjectTeamMutation();
  const [editProjectTeam] = useUpdateProjectTeamMutation();
  const [deleteProjectContact] = useDeleteProjectContactMutation();
  const [createProjectContact] = useCreateProjectContactMutation();
  const [editProjectContact] = useUpdateProjectContactMutation();
  const projectAddress = useAddress({ name: 'form' });
  return [
    Yup.object({
      title: Yup.string().required('Project name is required'),
      type: Yup.object().nullable().required('Project type is required'),
      template: Yup.object().nullable(),
      // type: Yup.object().required('Please select a project type').nullable(),
      contacts: Yup.array().of(
        Yup.object()
          .shape({
            contact: Yup.object()
              .nullable()
              .required('Please select a contact'),
          })
          .nullable()
      ),
      stakeholders: Yup.array().of(
        Yup.object()
          .shape({
            stakeholdertype: Yup.object()
              .nullable()
              .required('Please select a type'),
          })
          .nullable()
      ),
    }),
    [
      {
        name: '',
        label: '',
        itemType: 'paper',
        jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
        gridProps: { xs: 12, lg: 9 },
        array: [
          {
            name: 'project-details',
            label: 'Project Details',
            itemType: 'typography',
            jItemProps: { variant: 'overline' },
          },
          {
            name: 'project-details-bottom',
            label: '',
            itemType: 'divider',
            gridProps: { sx: { paddingTop: '0px !important' } },
          },
          // {
          //   name: '',
          //   label: '',
          //   itemType: 'paper',
          //   jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
          //   gridProps: { xs: 12 },
          //   array: [
          {
            name: 'details',
            label: 'Project Description',
            itemType: 'textfield',
            jItemProps: {
              multiline: true,
              minRows: 3,
              variant: 'outlined',
            },
            gridProps: { xs: 12 },
          },
          //   ],
          // },
          // {
          //   name: '',
          //   label: '',
          //   itemType: 'paper',
          //   jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
          //   gridProps: { xs: 12, lg: 6 },
          //   array: [
          {
            name: 'due_date',
            label: 'Expected completion date',
            itemType: 'datepicker',
            gridProps: { xs: 12, sm: 4 },
          },
          {
            name: 'form.billing_due_date',
            label: 'Billing Due Date',
            itemType: 'textfield',
            gridProps: { xs: 12, md: 4 },
          },
          {
            name: 'teams',
            itemType: 'autocomplete',
            options: (values: any) => {
              return (
                teams?.filter(
                  (t: Team) =>
                    !values?.find((tr: any) => t.id === tr?.team_r?.id)
                ) || []
              );
            },
            multiple: true,
            label: 'Team',
            loading: loading,
            gridProps: { xs: 12, sm: 4 },
            value:
              project_teams?.map(pt => ({
                ...pt,
                name: pt?.team_r?.name,
              })) || [],
            jItemProps: {
              disableClearable: true,
              onChange: async (items: any[]) => {
                if (items && Array.isArray(items) && items.length > 0) {
                  items.forEach(async (item: any) => {
                    if (!item.team) {
                      setLoading(true);
                      await createProjectTeam({
                        project: id,
                        team: item.id,
                      });
                      refetchProjectTeams();
                      // props?.afterSave && props?.afterSave()
                      setLoading(false);
                    }
                  });
                }
              },
              renderTags: (value: readonly string[], getTagProps: any) => {
                return value.map((option: any, index: number) => (
                  <Chip
                    variant="outlined"
                    color="secondary"
                    label={typeof option === 'string' ? option : option?.name}
                    size="small"
                    {...getTagProps({ index })}
                    onDelete={async props => {
                      setLoading(true);
                      if (option.id) {
                        await deleteProjectTeam(option.id);
                        getTagProps({ index }).onDelete(props);
                        refetchProjectTeams();
                        // props?.afterSave && props?.afterSave()
                      }
                      setLoading(false);
                    }}
                  />
                ));
              },
            },
          },
          {
            name: 'type',
            label: 'Project Type *',
            itemType: 'autocomplete',
            options: types,
            gridProps: (values: any) =>
              values?.type?.id &&
              market_segments?.filter(
                (ms: MarketSegment) => ms?.project_type === values?.type?.id
              ).length > 0
                ? { xs: 12, sm: 4 }
                : { xs: 12, sm: 6 },
            jItemProps: {
              onChange: (value: any, name: string, setFieldValue: Function) => {
                setFieldValue('market_segment', null);
              },
            },
          },
          {
            precondition: (values: any) =>
              values?.type?.id &&
              market_segments?.filter(
                (ms: MarketSegment) => ms?.project_type === values?.type?.id
              ).length > 0,
            name: 'market_segment',
            label: 'Market Segment',
            // disabled: edit,
            itemType: 'autocomplete',
            options: (values: any) =>
              market_segments?.filter(
                (ms: MarketSegment) => ms?.project_type === values?.type?.id
              ),
            gridProps: { xs: 12, sm: 4 },
          },
          {
            name: 'form.contract_type',
            label: 'Contract Type ',
            itemType: 'autocomplete',
            options: types,
            gridProps: (values: any) =>
              values?.type?.id &&
              market_segments?.filter(
                (ms: MarketSegment) => ms?.project_type === values?.type?.id
              ).length > 0
                ? { xs: 12, sm: 4 }
                : { xs: 12, sm: 6 },
          },
          {
            name: 'form.prevailing_wage',
            label: 'Prevailing Wage?',
            itemType: 'checkbox',
            jItemProps: {
              // tooltip:
              //   'User will receive email notifications on item and project completions.',
            },
            gridProps: { xs: 12, sm: 6 },
          },
          {
            precondition: (values: any) => values?.form?.prevailing_wage,
            name: 'form.prevailing_wage_date',
            label: 'Prevailing Wage Date',
            itemType: 'datepicker',
            gridProps: { xs: 12, sm: 6 },
          },
          ...projectAddress,
          {
            name: 'erp-info',
            label: 'ERP Information',
            itemType: 'typography',
            jItemProps: { variant: 'overline' },
          },
          {
            name: 'erp-info-bottom',
            label: '',
            itemType: 'divider',
            gridProps: { sx: { paddingTop: '0px !important' } },
          },
          {
            name: 'form.erp_proj_id',
            label: 'Project ID',
            itemType: 'textfield',
            gridProps: { xs: 12, md: 4 },
          },
          {
            name: 'form.erp_acct_id',
            label: 'Account ID',
            itemType: 'textfield',
            gridProps: { xs: 12, md: 4 },
          },
          {
            name: 'form.erp_sales_id',
            label: 'Sales Order ID',
            itemType: 'textfield',
            gridProps: { xs: 12, md: 4 },
          },
          //   ],
          // },
          //   ],
          // },
          // {
          //   name: '',
          //   label: '',
          //   itemType: 'paper',
          //   jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
          //   gridProps: { xs: 12, lg: 6 },
          //   array: projectAddress,
          // },
          // {
          //   name: '',
          //   label: '',
          //   itemType: 'paper',
          //   jItemProps: { paper: { sx: { py: 0, px: 0 }, elevation: 0 } },
          //   gridProps: { xs: 12, lg: 6 },
          //   array: [
          {
            name: 'project-financials',
            label: 'Project Financials',
            itemType: 'typography',
            jItemProps: { variant: 'overline' },
          },
          {
            name: 'project-financials-bottom',
            label: '',
            itemType: 'divider',
            gridProps: { sx: { paddingTop: '0px !important' } },
          },
          // {
          //   name: 'form.revenue',
          //   label: 'Estimated Revenue',
          //   itemType: 'textfield',
          //   // helpertext: "Add prices in $",
          //   jItemProps: {
          //     type: 'formatted_number',
          //     // type: 'number',
          //     InputProps: {
          //       startAdornment: '$',
          //     },
          //     inputProps: {
          //       inputMode: 'numeric',
          //     },
          //   },
          //   gridProps: { xs: 12, sm: 6 },
          // },
          // {
          //   name: 'form.margin',
          //   label: 'Estimated Margin',
          //   itemType: 'textfield',
          //   // input_type: 'number',
          //   // helpertext: "Add win rate in %",
          //   jItemProps: {
          //     type: 'number',
          //     InputProps: {
          //       startAdornment: '%',
          //     },
          //   },
          //   gridProps: { xs: 12, sm: 6 },
          // },
          // {
          //   name: 'form.realized_revenue',
          //   label: 'Realized Revenue',
          //   itemType: 'textfield',
          //   // helpertext: "Add prices in $",
          //   jItemProps: {
          //     type: 'formatted_number',
          //     // type: 'number',
          //     InputProps: {
          //       startAdornment: '$',
          //     },
          //     inputProps: {
          //       inputMode: 'numeric',
          //       pattern: '[0-9]*', // Only allow numeric input
          //     },
          //   },
          //   gridProps: { xs: 12, sm: 6 },
          // },
          // {
          //   name: 'form.realized_margin',
          //   label: 'Realized Margin',
          //   itemType: 'textfield',
          //   jItemProps: {
          //     type: 'number',
          //     InputProps: {
          //       startAdornment: '%',
          //     },
          //   },
          //   gridProps: { xs: 12, sm: 6 },
          // },
          {
            // precondition: (values: any) => !disable_type && values?.type === 'Default',
            name: 'form.custom_financials',
            itemType: 'fieldarrayselect',
            label: 'Financials Field',
            options: [
              'Budget Financials',
              'Bid/Quote Financials',
              'Contract Financials',
              'Realized Financials',
            ],
            jItemProps: {
              paper: { elevation: 0 },
            },
            gridProps: {
              // sx: { paddingTop: '10px !important' },
            },
            array: [
              // {
              //   precondition: (values: any) =>
              //     !values?.form?.custom_financials?.[values.index]?.fieldtype,
              //   name: 'fieldtype',
              //   label: 'Type',
              //   // disabled: edit,
              //   jItemProps: edit ? { type: 'local-disabled' } : {},
              //   itemType: 'autocomplete',
              //   options: ['Budget Financials'],
              // },
              {
                precondition: (values: any) =>
                  values?.form?.custom_financials?.['Budget Financials'] ||
                  values?.form?.revenue,
                name: '',
                label: 'Budget Financials',
                itemType: 'paper',
                jItemProps: { paper: { elevation: 0 } },
                gridProps: { xs: 12 },
                array: [
                  {
                    name: 'form.revenue',
                    label: 'Budget Revenue',
                    itemType: 'textfield',
                    // helpertext: "Add prices in $",
                    jItemProps: {
                      type: 'formatted_number',
                      // type: 'number',
                      InputProps: {
                        startAdornment: '$',
                      },
                      inputProps: {
                        inputMode: 'numeric',
                      },
                    },
                    gridProps: { xs: 12, sm: 6 },
                  },
                  {
                    name: 'form.margin',
                    label: 'Budget Margin',
                    itemType: 'textfield',
                    // input_type: 'number',
                    // helpertext: "Add win rate in %",
                    jItemProps: {
                      type: 'number',
                      InputProps: {
                        startAdornment: '%',
                      },
                    },
                    gridProps: { xs: 12, sm: 6 },
                  },
                ],
              },
              {
                precondition: (values: any) =>
                  values?.form?.custom_financials?.['Bid/Quote Financials'],
                name: '',
                label: 'Bid/Quote Financials',
                itemType: 'paper',
                jItemProps: { paper: { elevation: 0 } },
                gridProps: { xs: 12 },
                array: [
                  {
                    name: 'form.bidrevenue',
                    label: 'Bid/Quote Revenue',
                    itemType: 'textfield',
                    // helpertext: "Add prices in $",
                    jItemProps: {
                      type: 'formatted_number',
                      // type: 'number',
                      InputProps: {
                        startAdornment: '$',
                      },
                      inputProps: {
                        inputMode: 'numeric',
                      },
                    },
                    gridProps: { xs: 12, sm: 6 },
                  },
                  {
                    name: 'form.bidmargin',
                    label: 'Bid/Quote Margin',
                    itemType: 'textfield',
                    // input_type: 'number',
                    // helpertext: "Add win rate in %",
                    jItemProps: {
                      type: 'number',
                      InputProps: {
                        startAdornment: '%',
                      },
                    },
                    gridProps: { xs: 12, sm: 6 },
                  },
                ],
              },
              {
                precondition: (values: any) =>
                  values?.form?.custom_financials?.['Contract Financials'],
                name: '',
                label: 'Contract Financials',
                itemType: 'paper',
                jItemProps: { paper: { elevation: 0 } },
                gridProps: { xs: 12 },
                array: [
                  {
                    name: 'form.contractrevenue',
                    label: 'Contract Revenue',
                    itemType: 'textfield',
                    // helpertext: "Add prices in $",
                    jItemProps: {
                      type: 'formatted_number',
                      // type: 'number',
                      InputProps: {
                        startAdornment: '$',
                      },
                      inputProps: {
                        inputMode: 'numeric',
                      },
                    },
                    gridProps: { xs: 12, sm: 6 },
                  },
                  {
                    name: 'form.contractmargin',
                    label: 'Contract Margin',
                    itemType: 'textfield',
                    // input_type: 'number',
                    // helpertext: "Add win rate in %",
                    jItemProps: {
                      type: 'number',
                      InputProps: {
                        startAdornment: '%',
                      },
                    },
                    gridProps: { xs: 12, sm: 6 },
                  },
                ],
              },
              {
                precondition: (values: any) =>
                  values?.form?.custom_financials?.['Realized Financials'] ||
                  values?.form?.realized_revenue,
                name: '',
                label: 'Realized Financials',
                itemType: 'paper',
                jItemProps: { paper: { elevation: 0 } },
                gridProps: { xs: 12 },
                array: [
                  {
                    name: 'form.realized_revenue',
                    label: 'Realized Revenue',
                    itemType: 'textfield',
                    // helpertext: "Add prices in $",
                    jItemProps: {
                      type: 'formatted_number',
                      // type: 'number',
                      InputProps: {
                        startAdornment: '$',
                      },
                      inputProps: {
                        inputMode: 'numeric',
                        pattern: '[0-9]*', // Only allow numeric input
                      },
                    },
                    gridProps: { xs: 12, sm: 6 },
                  },
                  {
                    name: 'form.realized_margin',
                    label: 'Realized Margin',
                    itemType: 'textfield',
                    // input_type: 'number',
                    // helpertext: "Add win rate in %",
                    jItemProps: {
                      type: 'number',
                      InputProps: {
                        startAdornment: '%',
                      },
                    },
                    gridProps: { xs: 12, sm: 6 },
                  },
                ],
              },
            ],
          },
          //   ],
          // },

          {
            name: 'custom-fields',
            itemType: 'typography',
            jItemProps: {
              variant: 'overline',
              sx: { mb: '0 !important', pb: '0 !important' },
            },
            label: 'Additional Fields',
          },
          {
            name: 'additional-fields-bottom',
            label: '',
            itemType: 'divider',
            gridProps: { sx: { paddingTop: '0px !important' } },
          },
          {
            // precondition: (values: any) => !disable_type && values?.type === 'Default',
            name: 'form.custom_fields',
            itemType: 'fieldarray',
            label: 'Additional Field',
            gridProps: {
              // sx: { paddingTop: '10px !important' },
            },
            array: [
              {
                precondition: (values: any) =>
                  !values?.form?.custom_fields?.[values.index]?.fieldtype,
                name: 'fieldtype',
                label: 'Type',
                // disabled: edit,
                itemType: 'autocomplete',
                options: ['Owner', 'Custom'],
              },
              {
                precondition: (values: any) =>
                  values?.form?.custom_fields?.[values.index]?.fieldtype ===
                  'Custom',
                name: 'dynamic',
                label: 'Custom Field',
                jItemProps: {
                  textFieldType: 'custom',
                  // labelFunc: (name: any, values: any, formik: FormikHelpers<any>) => {
                  //   return getIn(values, name) || 'Please enter the field label...'
                  // },
                  InputProps: {
                    startAdornment: (
                      name: any,
                      values: any,
                      formik: FormikHelpers<any>
                    ) => {
                      if (!getIn(values, name)?.value) {
                        return (
                          <InputAdornment position="end">
                            <Tooltip title={`Reset type`}>
                              <IconButton
                                onClick={() => {
                                  formik.setFieldValue(name, undefined);
                                  formik.setFieldValue(
                                    name.replaceAll('dynamic', 'fieldtype'),
                                    undefined
                                  );
                                }}
                              >
                                <RestartAlt color="secondary" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        );
                      }
                    },
                  },
                },
                itemType: 'textfield',
              },
              {
                precondition: (values: any) =>
                  values?.form?.custom_fields?.[values.index]?.fieldtype ===
                  'Owner',
                name: 'owner_name',
                label: 'Owner',
                itemType: 'textfield',
              },
            ],
          },
        ],
      },
    ],
  ];
};
